import { cn } from "@/common/helpers/utils";
import { Table as TableComponent, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableBody } from "@/components/ui/table-body";
import { RowAction } from "@/components/ui/table-row-actions";
import { RowData } from "@tanstack/react-table";
import { AxiosError } from "axios";
import type { ComponentType, ReactElement } from "react";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import { GroupDetails } from "../predefined/_components/base-predefined-table.types";
import {
  DataTableInstance,
  DataTableRow,
  ImplementedColumnDef,
  TableBackground,
  tableBackgroundStyles,
} from "../types";
import { EmptyState } from "./empty-state";
import { wrapHeaderWithSortingComponent } from "./sortable-header";

interface DataTableContentProps<TData extends RowData & RowWithIdentifier> {
  table: DataTableInstance<TData>;
  data: TData[];
  isLoading: boolean;
  isError: AxiosError | boolean;
  activeFilterCount: number;
  hasDefaultFilters: boolean;
  hasNonDefaultFilters: boolean;
  searchTerm: string;
  tableBackground?: TableBackground;
  tableClassName?: string;
  onRowClick?: (row: DataTableRow<TData>) => void;
  onRowNavigate?: (row: DataTableRow<TData>) => string | undefined;
  subComponent?: ComponentType<{ row: DataTableRow<TData> }>;
  rowActions?: (row: DataTableRow<TData>) => RowAction[];
  onClearAllFilters?: () => void;
  onResetAllFilters?: () => void;
  onClearSearch: () => void;
  groupByCallback?: (rows: TData[]) => (GroupDetails<TData> | TData)[];
  emptyStateProps?: Parameters<typeof EmptyState>[0];
}

export function DataTableContent<TData extends RowData & RowWithIdentifier>({
  table,
  data,
  isLoading,
  isError,
  activeFilterCount,
  hasDefaultFilters,
  hasNonDefaultFilters,
  searchTerm,
  tableBackground,
  tableClassName,
  onRowClick,
  onRowNavigate,
  subComponent,
  rowActions,
  onClearAllFilters,
  onResetAllFilters,
  onClearSearch,
  groupByCallback,
  emptyStateProps,
}: DataTableContentProps<TData>): ReactElement {
  const hasFilters = activeFilterCount > 0;
  const hasSearch = searchTerm.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={cn("border-border relative h-full overflow-hidden rounded-lg border", tableClassName)}>
        {data.length === 0 && !isLoading ? (
          <EmptyState
            hasFilters={hasFilters}
            hasDefaultFilters={hasDefaultFilters}
            hasNonDefaultFilters={hasNonDefaultFilters}
            hasSearch={hasSearch}
            searchTerm={searchTerm}
            onClearFilters={onClearAllFilters}
            onResetFilters={onResetAllFilters}
            onClearSearch={hasSearch ? onClearSearch : undefined}
            {...emptyStateProps}
          />
        ) : (
          <div
            className={cn(
              "h-full scrollbar-none overflow-auto rounded-lg",
              "sm:scrollbar-thin [&::-webkit-scrollbar]:hidden sm:[&::-webkit-scrollbar]:block",
            )}
          >
            <div className="w-fit min-w-full">
              <div className="sticky top-0 z-10 h-0">
                <div
                  className={cn(
                    "h-9 rounded-t-lg backdrop-blur-sm",
                    tableBackgroundStyles[tableBackground ?? TableBackground.Background],
                  )}
                />
              </div>
              <TableComponent>
                <TableHeader className="sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="border-0 hover:bg-transparent">
                      {headerGroup.headers.map((header) => {
                        const columnDef = header.column.columnDef as ImplementedColumnDef<TData>;
                        const size = header.getSize();
                        const isFluid = columnDef.size === 0 || columnDef.size === undefined || columnDef.size === null;

                        return (
                          <TableHead
                            key={header.id}
                            style={{
                              width: isFluid ? undefined : size,
                              minWidth: isFluid ? undefined : columnDef.minSize,
                              maxWidth: isFluid ? undefined : columnDef.maxSize,
                            }}
                            colSpan={header.colSpan}
                            className={cn(isFluid && "w-full")}
                          >
                            {header.isPlaceholder ? null : wrapHeaderWithSortingComponent(header)}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody
                  table={table}
                  isLoading={isLoading}
                  hasError={isError}
                  rowClick={onRowClick}
                  rowNavigateFn={onRowNavigate}
                  subComponent={subComponent}
                  rowActions={rowActions}
                  groupByCallback={groupByCallback}
                />
              </TableComponent>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
